import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { getApiErrorMessage, isEmailNotVerifiedError } from '../lib/apiError';
import { analytics } from '../lib/analytics';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Eye, Edit, Trash2, Send, FileText, ExternalLink, Download, Bell, Copy, DollarSign, Loader2, FileDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Invoices = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  
  // Payment dialog state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await api.get('/invoices');
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Build query params based on current filters
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await api.get(`/invoices/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoices_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const notifyVerifyEmail = () => {
    toast.error('Verify your email before sending invoices. Check your inbox for the verification link.', {
      action: {
        label: 'Resend email',
        onClick: async () => {
          try {
            await api.post('/auth/resend-verification');
            toast.success('Verification email sent — please check your inbox.');
          } catch (err) {
            toast.error(getApiErrorMessage(err, 'Could not send verification email.'));
          }
        },
      },
      duration: 8000,
    });
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/send`, { origin_url: window.location.origin });
      analytics.invoiceSent();
      toast.success('Invoice sent successfully! Email notification sent to client.');
      fetchInvoices();
    } catch (error) {
      if (isEmailNotVerifiedError(error)) {
        notifyVerifyEmail();
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to send invoice'));
      }
    }
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/remind`, { origin_url: window.location.origin });
      toast.success('Payment reminder sent to client!');
    } catch (error) {
      if (isEmailNotVerifiedError(error)) {
        notifyVerifyEmail();
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to send reminder'));
      }
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await api.put(`/invoices/${invoiceId}`, { status: 'paid' });
      toast.success('Invoice marked as paid');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handleDuplicateInvoice = async (invoiceId) => {
    try {
      const response = await api.post(`/invoices/${invoiceId}/duplicate`);
      toast.success(`Invoice duplicated as ${response.data.invoice_number}`);
      navigate(`/invoices/${response.data.invoice_id}/edit`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to duplicate invoice'));
    }
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentAmount(invoice.balance_due?.toString() || invoice.total.toString());
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMethod('');
    setPaymentNotes('');
    setShowPaymentDialog(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    setSavingPayment(true);
    try {
      await api.post(`/invoices/${selectedInvoice.invoice_id}/payments`, {
        amount: parseFloat(paymentAmount),
        payment_date: paymentDate,
        payment_method: paymentMethod || null,
        notes: paymentNotes || null
      });
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      fetchInvoices();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to record payment'));
    } finally {
      setSavingPayment(false);
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getPublicInvoiceUrl = (invoiceId) => {
    return `${window.location.origin}/pay/${invoiceId}`;
  };

  const copyPaymentLink = (invoiceId) => {
    navigator.clipboard.writeText(getPublicInvoiceUrl(invoiceId));
    toast.success('Payment link copied to clipboard');
  };

  return (
    <Layout>
      <div className="p-6 lg:p-12 max-w-7xl mx-auto space-y-8" data-testid="invoices-page">
        <EmailVerificationBanner />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl tracking-tight">Invoices</h1>
            <p className="text-muted-foreground mt-1">Manage and track all your invoices</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleExportCSV} disabled={exporting} className="gap-2" data-testid="export-csv-btn">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Export CSV
            </Button>
            <Link to="/invoices/new">
              <Button className="gap-2 shadow-lg shadow-primary/20" data-testid="create-invoice-btn">
                <Plus className="w-4 h-4" />
                New Invoice
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search invoices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="partially_paid">Partially Paid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-muted-foreground">Loading invoices...</div>
            ) : filteredInvoices.length === 0 ? (
              (searchTerm || statusFilter !== 'all') ? (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="font-medium text-lg mb-2">No invoices found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your search or filters
                  </p>
                </div>
              ) : (
                <div className="m-6 rounded-2xl bg-primary text-primary-foreground p-12 text-center border border-foreground/10">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-primary-foreground/10 flex items-center justify-center">
                    <img
                      src="https://res.cloudinary.com/nwvsq3gc/image/upload/e_trim/w_200,q_auto,f_auto/v1788391503/CC_book.png"
                      alt="Hand-illustrated book character"
                      className="w-14 h-14 object-contain"
                      data-testid="invoices-empty-illustration"
                    />
                  </div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] opacity-80 mb-3 block">
                    ● &nbsp; Nothing here yet
                  </span>
                  <h3 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-3 leading-tight">
                    Send your first invoice.
                  </h3>
                  <p className="opacity-90 mb-8 max-w-md mx-auto">
                    Two minutes from now, you could have a beautifully-branded invoice in your client&apos;s inbox.
                  </p>
                  <Link to="/invoices/new">
                    <Button size="lg" variant="secondary" className="rounded-full px-8">Create Invoice</Button>
                  </Link>
                </div>
              )
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead className="hidden sm:table-cell">Client</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead className="hidden sm:table-cell">Due Date</TableHead>
                    <TableHead className="hidden sm:table-cell">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => {
                    const today = new Date().toISOString().split('T')[0];
                    const isOverdue = invoice.status !== 'paid' && invoice.status !== 'draft' && invoice.due_date < today;
                    
                    return (
                    <TableRow key={invoice.invoice_id} data-testid={`invoice-row-${invoice.invoice_id}`} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/invoices/${invoice.invoice_id}`)}>
                      <TableCell className="font-mono text-sm">
                        <div>
                          <span>{invoice.invoice_number}</span>
                          <p className="font-sans text-xs text-muted-foreground sm:hidden">{invoice.client_name}</p>
                          <div className="mt-1 sm:hidden">
                            <Badge variant="secondary" className={getStatusColor(invoice.status)}>
                              {invoice.status}
                            </Badge>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div>
                          <p className="font-medium">{invoice.client_name}</p>
                          <p className="text-xs text-muted-foreground">{invoice.client_email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono font-medium">
                        <div>
                          <span>{formatCurrency(invoice.total, invoice.currency)}</span>
                          {invoice.balance_due !== undefined && invoice.balance_due !== invoice.total && invoice.balance_due > 0 && (
                            <p className="text-xs text-muted-foreground">Due: {formatCurrency(invoice.balance_due, invoice.currency)}</p>
                          )}
                          {isOverdue && invoice.late_fee_accrued > 0 && (
                            <p className="text-xs text-destructive font-medium">+{formatCurrency(invoice.late_fee_accrued, invoice.currency)} late fee</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div>
                          <span>{formatDate(invoice.due_date)}</span>
                          {isOverdue && (
                            <Badge variant="destructive" className="ml-2 text-xs">OVERDUE</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge variant="secondary" className={getStatusColor(invoice.status)}>
                          {invoice.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" data-testid={`invoice-menu-${invoice.invoice_id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${invoice.invoice_id}`); }}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDownloadPDF(invoice.invoice_id, invoice.invoice_number); }}>
                              <Download className="w-4 h-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); copyPaymentLink(invoice.invoice_id); }}>
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Copy Payment Link
                            </DropdownMenuItem>
                            {invoice.status !== 'paid' && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${invoice.invoice_id}/edit`); }}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Invoice
                              </DropdownMenuItem>
                            )}
                            {invoice.status === 'draft' && (
                              <DropdownMenuItem
                                onClick={(e) => { e.stopPropagation(); handleSendInvoice(invoice.invoice_id); }}
                                disabled={!user?.email_verified}
                                title={!user?.email_verified ? 'Verify your email to unlock sending.' : undefined}
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Send Invoice
                              </DropdownMenuItem>
                            )}
                            {(invoice.status === 'sent' || invoice.status === 'partially_paid') && (
                              <>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openPaymentDialog(invoice); }}>
                                  <DollarSign className="w-4 h-4 mr-2" />
                                  Record Payment
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); handleSendReminder(invoice.invoice_id); }}
                                  disabled={!user?.email_verified}
                                  title={!user?.email_verified ? 'Verify your email to unlock sending.' : undefined}
                                >
                                  <Bell className="w-4 h-4 mr-2" />
                                  Send Reminder
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleMarkPaid(invoice.invoice_id); }}>
                                  <FileText className="w-4 h-4 mr-2" />
                                  Mark as Fully Paid
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicateInvoice(invoice.invoice_id); }}>
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate Invoice
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); handleDeleteInvoice(invoice.invoice_id); }}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )})}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              {selectedInvoice && (
                <>Record a payment for invoice {selectedInvoice.invoice_number}</>
              )}
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4 py-4">
              <div className="bg-muted p-4 rounded-lg space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Invoice Total:</span>
                  <span className="font-medium">{formatCurrency(selectedInvoice.total)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount Paid:</span>
                  <span className="font-medium">{formatCurrency(selectedInvoice.amount_paid || 0)}</span>
                </div>
                <div className="flex justify-between font-medium border-t pt-1 mt-1">
                  <span>Balance Due:</span>
                  <span>{formatCurrency(selectedInvoice.balance_due || selectedInvoice.total)}</span>
                </div>
              </div>

              <div>
                <Label htmlFor="payment-amount">Payment Amount *</Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="payment-amount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={selectedInvoice.balance_due || selectedInvoice.total}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="pl-7"
                    data-testid="payment-amount-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="payment-date">Payment Date *</Label>
                <Input
                  id="payment-date"
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="mt-1.5"
                  data-testid="payment-date-input"
                />
              </div>

              <div>
                <Label htmlFor="payment-method">Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="mt-1.5" data-testid="payment-method-select">
                    <SelectValue placeholder="Select method (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="payment-notes">Notes (optional)</Label>
                <Input
                  id="payment-notes"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="e.g., Reference number, memo"
                  className="mt-1.5"
                  data-testid="payment-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} disabled={savingPayment} data-testid="save-payment-btn">
              {savingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Invoices;
